import { EditorSelection } from '@codemirror/state';
import type { EditorView } from '@codemirror/view';

/**
 * Toolbar actions, expressed as edits to the markdown text itself.
 *
 * The document stays a plain string all the way down — the toolbar is a
 * shortcut for typing the syntax, never a separate rich-text model. That is
 * what keeps last-write-wins sync trivial and export free.
 */

/** Wraps the selection, or unwraps it when the marks are already there. */
export function toggleWrap(view: EditorView, mark: string, closing = mark) {
  const { state } = view;
  const changes = state.changeByRange((range) => {
    const before = state.sliceDoc(range.from - mark.length, range.from);
    const after = state.sliceDoc(range.to, range.to + closing.length);

    // Already wrapped: strip the marks and keep the selection on the text.
    if (before === mark && after === closing) {
      return {
        changes: [
          { from: range.from - mark.length, to: range.from },
          { from: range.to, to: range.to + closing.length },
        ],
        range: EditorSelection.range(range.from - mark.length, range.to - mark.length),
      };
    }

    return {
      changes: [
        { from: range.from, insert: mark },
        { from: range.to, insert: closing },
      ],
      range: EditorSelection.range(range.from + mark.length, range.to + mark.length),
    };
  });

  view.dispatch(changes, { scrollIntoView: true });
  view.focus();
}

/**
 * Applies a line prefix to every line the selection touches. Re-applying the
 * same prefix removes it, so the buttons behave as toggles rather than as an
 * accumulating stack of `###`.
 */
export function toggleLinePrefix(view: EditorView, prefix: string, strip: RegExp) {
  const { state } = view;
  const changes: Array<{ from: number; to: number; insert: string }> = [];

  for (const range of state.selection.ranges) {
    const first = state.doc.lineAt(range.from).number;
    const last = state.doc.lineAt(range.to).number;

    for (let n = first; n <= last; n++) {
      const line = state.doc.line(n);
      const existing = strip.exec(line.text);
      const already = existing?.[0] === prefix;
      changes.push({
        from: line.from,
        to: line.from + (existing?.[0].length ?? 0),
        insert: already ? '' : prefix,
      });
    }
  }

  view.dispatch({ changes }, { scrollIntoView: true });
  view.focus();
}

const HEADING = /^#{1,6}\s+/;
const QUOTE = /^>\s?/;
const BULLET = /^[-*+]\s+(?:\[[ xX]]\s+)?/;
const ORDERED = /^\d+\.\s+/;
const TASK = /^[-*+]\s+\[[ xX]]\s+/;

export const heading = (view: EditorView, level: 1 | 2 | 3) =>
  toggleLinePrefix(view, `${'#'.repeat(level)} `, HEADING);

export const bold = (view: EditorView) => toggleWrap(view, '**');
export const italic = (view: EditorView) => toggleWrap(view, '_');
export const strikethrough = (view: EditorView) => toggleWrap(view, '~~');
export const inlineCode = (view: EditorView) => toggleWrap(view, '`');

export const bulletList = (view: EditorView) => toggleLinePrefix(view, '- ', BULLET);
export const orderedList = (view: EditorView) => toggleLinePrefix(view, '1. ', ORDERED);
export const taskList = (view: EditorView) => toggleLinePrefix(view, '- [ ] ', TASK);
export const quote = (view: EditorView) => toggleLinePrefix(view, '> ', QUOTE);

/** Fenced block on its own lines, with the cursor landing inside it. */
export function codeBlock(view: EditorView) {
  const { state } = view;
  const range = state.selection.main;
  const selected = state.sliceDoc(range.from, range.to);
  const line = state.doc.lineAt(range.from);
  const lead = line.from === range.from ? '' : '\n';
  const insert = `${lead}\`\`\`\n${selected}\n\`\`\`\n`;

  view.dispatch(
    {
      changes: { from: range.from, to: range.to, insert },
      selection: { anchor: range.from + lead.length + 4 + selected.length },
    },
    { scrollIntoView: true },
  );
  view.focus();
}

/** `[label](url)` — the selection becomes the label, cursor lands on the URL. */
export function link(view: EditorView) {
  const { state } = view;
  const range = state.selection.main;
  const label = state.sliceDoc(range.from, range.to) || 'link';
  const insert = `[${label}](url)`;
  const urlAt = range.from + label.length + 3;

  view.dispatch(
    {
      changes: { from: range.from, to: range.to, insert },
      selection: { anchor: urlAt, head: urlAt + 3 },
    },
    { scrollIntoView: true },
  );
  view.focus();
}

/**
 * Inserts an inline image reference at the cursor. Attachments live in the
 * gallery by default; this is for pinning one at a specific point in the text.
 */
export function imageRef(view: EditorView, name = 'image') {
  const range = view.state.selection.main;
  const insert = `![${name}](${name}.webp)`;
  view.dispatch(
    {
      changes: { from: range.from, to: range.to, insert },
      selection: { anchor: range.from + insert.length },
    },
    { scrollIntoView: true },
  );
  view.focus();
}

/** Scrolls a heading into view and puts the cursor on it. */
export function goToLine(view: EditorView, lineNumber: number) {
  const line = view.state.doc.line(Math.min(lineNumber, view.state.doc.lines));
  view.dispatch({
    selection: { anchor: line.from },
    effects: [],
    scrollIntoView: true,
  });
  view.focus();
}
